/**
 * index.js - Application Entry Point
 * 
 * This is where React mounts the app to the DOM.
 * We wrap everything in our context providers here.
 * 
 * UPDATED: Now initializes Supabase data before rendering.
 * UPDATED: PWA service worker registration added.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import dataStore from './data/dataStore';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Get the root element from index.html
const root = ReactDOM.createRoot(document.getElementById('root'));

// Show loading while initializing
root.render(
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#f5f5f5'
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '24px', marginBottom: '10px' }}>Loading CPLC...</div>
      <div style={{ fontSize: '14px', color: '#666' }}>Connecting to database...</div>
    </div>
  </div>
);

// Initialize data from Supabase, then render the app
dataStore.initializeData().then(() => {
  console.log('Data initialized, rendering app...');
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <AuthProvider>
          <App />
        </AuthProvider>
      </BrowserRouter>
    </React.StrictMode>
  );
  
  // Register service worker for PWA functionality
  // Only active in production builds
  serviceWorkerRegistration.register({
    onSuccess: () => console.log('CPLC PWA: Ready for offline use'),
    onUpdate: (registration) => {
      console.log('CPLC PWA: New version available, reloading...');
      // Auto-reload when new version is available
      if (registration && registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      // Reload after a brief delay to allow SW to activate
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  });
  
}).catch(error => {
  console.error('Failed to initialize data:', error);
  root.render(
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#fff0f0'
    }}>
      <div style={{ textAlign: 'center', color: '#c00' }}>
        <div style={{ fontSize: '24px', marginBottom: '10px' }}>Connection Error</div>
        <div style={{ fontSize: '14px' }}>Unable to connect to database. Please try again later.</div>
        <button 
          onClick={() => window.location.reload()} 
          style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
});
